"use client";

import React, { useState } from "react";
import { 
  History, 
  ChevronRight, 
  ChevronDown, 
  X, 
  Trash2, 
  ExternalLink,
  Clock,
  Search,
  Tag
} from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";
import { ScrollArea } from "./scroll-area";
import { Separator } from "./separator";
import { useSearchHistory, SearchHistoryItem } from "../hooks/useSearchHistory";
import { getResultIcon } from "../lib/search-utils";

interface SearchHistoryDrawerProps {
  onItemClick?: (item: SearchHistoryItem) => void;
  className?: string;
}

export function SearchHistoryDrawer({ onItemClick, className = "" }: SearchHistoryDrawerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { history, removeFromHistory, clearHistory, getRecentHistory } = useSearchHistory();

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleItemClick = (item: SearchHistoryItem) => {
    onItemClick?.(item);
    // Navigate to the URL
    window.location.href = item.url;
  };

  const handleRemoveItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    removeFromHistory(itemId);
  };

  const handleClearAll = () => {
    if (confirm("Clear all search history?")) {
      clearHistory();
    }
  };

  const recentHistory = getRecentHistory(20);

  if (history.length === 0) {
    return null;
  }

  return (
    <div className={`bg-card border-l border-gray-200 transition-all duration-300 ${className} ${
      isExpanded ? "w-72 lg:w-80" : "w-12"
    }`}>
      {/* Drawer Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {isExpanded && (
            <div className="flex items-center space-x-2">
              <History className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-card-foreground">Recent</span>
              <Badge variant="secondary" className="text-xs">
                {recentHistory.length}
              </Badge>
            </div>
          )}
          <div className="flex items-center space-x-1">
            {isExpanded && history.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                title="Clear all history"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-6 w-6 p-0"
            >
              {isExpanded ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3 transform -rotate-90" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Drawer Content */}
      {isExpanded && (
        <div className="h-full flex flex-col">
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {recentHistory.map((item, index) => (
                <div key={item.id}>
                  <div
                    onClick={() => handleItemClick(item)}
                    className="group relative p-2 rounded-md hover:bg-card-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-0.5">
                        {getResultIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-card-foreground truncate">
                          {item.title}
                        </div>
                        {item.subtitle && (
                          <div className="text-xs text-gray-500 truncate">
                            {item.subtitle}
                          </div>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="outline" className="text-xs capitalize">
                            {item.type.replace("_", " ")}
                          </Badge>
                          <div className="flex items-center space-x-1 text-xs text-gray-400">
                            <Clock className="h-2.5 w-2.5" />
                            <span>{formatTimeAgo(item.timestamp)}</span>
                          </div>
                        </div>
                        {item.searchQuery && (
                          <div className="flex items-center space-x-1 mt-1">
                            <Search className="h-2.5 w-2.5 text-gray-400" />
                            <span className="text-xs text-gray-400 italic">
                              "{item.searchQuery}"
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 flex items-center space-x-1">
                        <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-gray-600" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleRemoveItem(e, item.id)}
                          className="h-4 w-4 p-0 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all"
                        >
                          <X className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {index < recentHistory.length - 1 && (
                    <Separator className="my-1" />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Drawer Footer */}
          {history.length > 0 && (
            <div className="p-3 border-t border-gray-200">
              <div className="text-xs text-gray-500 text-center">
                {history.length} item{history.length !== 1 ? 's' : ''} in history
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collapsed State Icon */}
      {!isExpanded && (
        <div className="p-3 flex justify-center">
          <div className="relative">
            <History className="h-5 w-5 text-gray-600" />
            {history.length > 0 && (
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-medium">
                  {history.length > 9 ? "9+" : history.length}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
